// The lab report inbox's deep link into a person's Test (TGCLOUD-1866 Phase 3; plan, "Hand-off to
// Phase 2" step 1):
//
//   /admin/pedigree/:familyId/?familyMemberId=<G-number>&testId=<Test id | new>&labReportInboxItem=<id>
//   with router state { selectedFamilyMemberId: <G-number> }
//
// The older deep links (?familyMemberId=&testId=, from Pathways and notifications) open a Test as
// soon as the family's records load, with NOBODY selected on the chart. The Test form reads a lab
// report only for a real person address (recordAddress.js: nobody selected is 'none'), so opened
// that way the report could never be read. This link therefore waits for the PERSON first: the
// chart selects them from the router state (ChartBox.jsx; from `familyMemberId` when the state is
// missing, a link opened in a new tab), and only then is the Test opened, an existing one once the
// records have loaded, or a new one at once.
//
// ONE PERSON OF A REPORT ABOUT SEVERAL (TGCLOUD-1868, "Phase E contracts, in detail for the build":
// "The deep link adds `&individual=<key>&pregnancyId=&fetusId=`"). A trio's or a prenatal report is
// matched in the inbox one person at a time, and each is read into a Test of their own:
//
//   &individual=<key>                      which person of the report the Test is for, by the key the
//                                          reader gives them (backend rules.INDIVIDUAL_KEY_PATTERN)
//   &pregnancyId=<id>&fetusId=<id>         a FETUS's fetus record, both or neither: its result goes on
//                                          its MOTHER's record (the link's person), on a Test of that
//                                          pregnancy and fetus, never on a Test of hers
//
// Which Test fits whom is the server's rule (labReportIntake.individualFitProblem), kept here so a
// link never opens a Test the hand-off would only refuse: a fetus's Test is a NEW one opened scoped
// to its fetus (the same path as "record an event for this fetus", ClinicalNotes openScopedRecord),
// or an existing Test carrying exactly that pregnancy and fetus; anyone else's is a Test that is not
// about a pregnancy (prenatalScope.carriesPrenatalScope). A link naming no individual is the one it
// always was. Anything ill formed is no link at all.
//
// Pure, so the rule is tested without mounting the sidebar.

import { carriesPrenatalScope } from './forms/prenatalScope';

const OBJECT_ID = /^[0-9a-f]{24}$/i;

/** `testId=new`: a new Test, its id minted by the form (TestForm ensureTestId). */
export const NEW_TEST = 'new';

export const LAB_REPORT_INBOX_PARAM = 'labReportInboxItem';
export const INDIVIDUAL_PARAM = 'individual';
// The hand-off's own parameters: the item, and the person of the report it is for.
const HAND_OFF_PARAMS = Object.freeze([
  LAB_REPORT_INBOX_PARAM,
  INDIVIDUAL_PARAM,
  'pregnancyId',
  'fetusId'
]);

// A person's key on a report about several (backend rules.INDIVIDUAL_KEY_PATTERN, bound by
// labReportInboxLink.test.js): their role where the report has one of them, a sibling, a child or a
// fetus by their place among them (two fetuses are `fetus-1` and `fetus-2`: TGCLOUD-1868, decision
// 23), otherwise their place among all of them.
export const INDIVIDUAL_KEY_PATTERN =
  /^(?:fetus|mother|father|proband|(?:sibling|child|fetus|row)-[1-9][0-9]?)$/;
const ROLE_KEYS = Object.freeze(['fetus', 'mother', 'father', 'proband']);

/**
 * The role a key says, where it says one: `mother` is the mother, `sibling-2` a sibling, `fetus-2`
 * a fetus; a `row-3` says none (the person may be a twin, a partner, anyone). Null for anything
 * else.
 */
export function roleOfIndividualKey(key) {
  if (typeof key !== 'string') return null;
  if (ROLE_KEYS.includes(key)) return key;
  const numbered = /^(sibling|child|fetus)-\d+$/.exec(key);
  return numbered ? numbered[1] : null;
}

/**
 * @param {string} search location.search
 * @returns {{ itemId: string, familyMemberId: string, testId: string, individual?: { key: string,
 *   role: string|null, pregnancyId?: string, fetusId?: string } } | null} null unless every part is
 *   there and well formed. `individual` only for a link naming one (a fetus's with its fetus record,
 *   and the role `fetus`, which only a fetus's link carries).
 */
export function readLabReportInboxLink(search) {
  const query = new URLSearchParams(search || '');
  const itemId = query.get(LAB_REPORT_INBOX_PARAM);
  const familyMemberId = query.get('familyMemberId');
  const testId = query.get('testId');
  if (!itemId || !OBJECT_ID.test(itemId)) return null;
  if (!familyMemberId || !familyMemberId.trim()) return null;
  if (testId !== NEW_TEST && !OBJECT_ID.test(testId || '')) return null;
  const key = query.get(INDIVIDUAL_PARAM);
  const pregnancyId = query.get('pregnancyId');
  const fetusId = query.get('fetusId');
  // A fetus record is named only for a person of the report, and whole.
  if (key === null) {
    return pregnancyId === null && fetusId === null
      ? { itemId, familyMemberId, testId }
      : null;
  }
  if (!INDIVIDUAL_KEY_PATTERN.test(key)) return null;
  if ((pregnancyId === null) !== (fetusId === null)) return null;
  if (
    pregnancyId !== null &&
    !(OBJECT_ID.test(pregnancyId) && OBJECT_ID.test(fetusId))
  ) {
    return null;
  }
  return {
    itemId,
    familyMemberId,
    testId,
    individual: {
      key,
      role: pregnancyId !== null ? 'fetus' : roleOfIndividualKey(key),
      ...(pregnancyId !== null ? { pregnancyId, fetusId } : {})
    }
  };
}

/** Is there an inbox item in the address at all, well formed or not? */
export const hasLabReportInboxParam = (search) =>
  new URLSearchParams(search || '').has(LAB_REPORT_INBOX_PARAM);

/**
 * The address with the inbox item, and the person of the report it was for, taken out and
 * everything else kept, once the link is acted on: a reload must not hand the report over a second
 * time (after the Test is saved the item is linked, and a second hand-off could only be refused).
 */
export function withoutLabReportInboxItem(search) {
  const query = new URLSearchParams(search || '');
  for (const param of HAND_OFF_PARAMS) query.delete(param);
  const rest = query.toString();
  return rest ? `?${rest}` : '';
}

/** What the clinician is told when the link names a Test its person of the report does not fit. */
export const UNFIT_LINK_MESSAGES = Object.freeze({
  fetus:
    'The Test chosen in the Lab Report Inbox is not a Test for this fetus, so the fetus’s result was not opened on it. Open it from the Lab Report Inbox again and choose the fetus’s Test, or a new one.',
  other:
    'The Test chosen in the Lab Report Inbox is about a pregnancy, so this person’s result was not opened on it. Open it from the Lab Report Inbox again and choose a Test of their own, or a new one.'
});

// The fetus record a link names, as a Test's prenatal scope: { pregnancyId, fetusId }, or null.
const fetusScopeOf = (individual) =>
  individual && individual.pregnancyId && individual.fetusId
    ? { pregnancyId: individual.pregnancyId, fetusId: individual.fetusId }
    : null;

/**
 * Why a person of the report does not fit a Test, or null when they do (the server's rule, above):
 * 'fetus' for a fetus's result and a Test that is not of its fetus record (or a link that names no
 * fetus record), 'other' for anyone else's result and a Test about a pregnancy.
 */
export function individualTestMisfit(individual, record) {
  if (!individual) return null;
  if (individual.role === 'fetus') {
    const scope = fetusScopeOf(individual);
    return scope &&
      String(record?.pregnancyId ?? '') === scope.pregnancyId &&
      String(record?.fetusId ?? '') === scope.fetusId
      ? null
      : 'fetus';
  }
  return carriesPrenatalScope(record) ? 'other' : null;
}

/**
 * What to do about the link now.
 *
 * @param {object} args
 * @param {object|null} args.link            readLabReportInboxLink's answer
 * @param {string} args.selectedAddress      the selected person's record address (recordAddressFor)
 * @param {boolean} args.recordsLoaded       the family's clinical records have loaded
 * @param {object[]} args.records            those records
 * @returns {{ kind: 'none' } | { kind: 'wait' } | { kind: 'open-new', scope?: object }
 *   | { kind: 'open-existing', record: object } | { kind: 'missing' }
 *   | { kind: 'unfit', reason: 'fetus'|'other' }} `scope`: the fetus record a new Test is opened for
 *   (a fetus's link); 'unfit': the Test is not one the person of the report the link names can be
 *   read into (individualTestMisfit)
 */
export function labReportInboxLinkStep({
  link,
  selectedAddress,
  recordsLoaded,
  records
}) {
  if (!link) return { kind: 'none' };
  // The person first, always: the lab-report parts of the form need their address.
  if (String(selectedAddress ?? '') !== String(link.familyMemberId)) {
    return { kind: 'wait' };
  }
  const individual = link.individual || null;
  if (link.testId === NEW_TEST) {
    if (!individual) return { kind: 'open-new' };
    // A fetus's result on a new Test of its own fetus record; anyone else's on a new Test of theirs.
    const scope = fetusScopeOf(individual);
    const misfit = individualTestMisfit(individual, scope || {});
    if (misfit) return { kind: 'unfit', reason: misfit };
    return scope ? { kind: 'open-new', scope } : { kind: 'open-new' };
  }
  if (!recordsLoaded) return { kind: 'wait' };
  const record = (records || []).find(
    (r) => r?.noteType === 'Test' && String(r._id) === String(link.testId)
  );
  // A Test that is not this person's is not opened for them, whatever the address says.
  if (!record || String(record.familyMemberId) !== String(link.familyMemberId))
    return { kind: 'missing' };
  const misfit = individualTestMisfit(individual, record);
  if (misfit) return { kind: 'unfit', reason: misfit };
  return { kind: 'open-existing', record };
}
